import { portalFetch } from "@/lib/portalApi";
import type { VatTreatment } from "@/lib/countries";

/** `cp01.Service.Category`. `managed_service` = a Larasoft-operated service
 * such as the Managed database (label via `categoryLabel` in @/lib/serviceCategory). */
export type ServiceCategory = "platform" | "connector" | "managed_service" | "workflow_template";

// ============================================================================
// Types - Users
// ============================================================================

export interface PlatformUser {
  id: string;
  email: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  email_verified: boolean;
  created_at: string;
  last_login_at: string | null;
  organisations: {
    organisation_id: string;
    organisation_name: string;
    role: string;
  }[];
}

export interface UsersListResponse {
  items: PlatformUser[];
  total: number;
}

// ============================================================================
// Types - Organisations
// ============================================================================

export interface Organisation {
  id: string;
  name: string;
  slug: string;
  description?: string;
  time_zone?: string;
  settings?: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user_count?: number;
  subscription_count?: number;
  country_code?: string;
  billing_currency?: string;
  /**
   * Per-organisation override of the country's VAT treatment; null/absent means
   * "inherit the country default". Set only via the admin `/billing` PATCH.
   */
  vat_treatment?: VatTreatment | null;
}

export interface OrganisationUser {
  id: string;
  email: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  is_owner: boolean;
  created_at: string;
}

export interface OrganisationsListResponse {
  items: Organisation[];
  total: number;
}

// ============================================================================
// Types - API Keys
// ============================================================================

export type APIKeyScope =
  | "rag:query"
  | "rag:ingest"
  | "documents:read"
  | "documents:write"
  | "agentic:execute"
  | "mcp:query";

export type RateLimitTier = "free" | "standard" | "enterprise";

export interface ExternalAPIKey {
  id: string;
  organisation_id: string;
  name: string;
  key_prefix: string;
  scopes: APIKeyScope[];
  rate_limit_tier: RateLimitTier;
  is_active: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface ExternalAPIKeyCreateRequest {
  organisation_id: string;
  name: string;
  scopes: APIKeyScope[];
  rate_limit_tier: RateLimitTier;
  expires_at: string | null;
}

export interface ExternalAPIKeyCreateResponse {
  id: string;
  key: string;
  key_prefix: string;
}

export interface ExternalAPIKeyListResponse {
  items: ExternalAPIKey[];
  total: number;
}

// ============================================================================
// Types - Subscriptions
// ============================================================================

export interface Subscription {
  organisation_id: string;
  organisation_name: string;
  service_name: string;
  service_display_name: string;
  tier: string;
  status: "active" | "cancelled" | "pending";
  current_period_start: string | null;
  current_period_end: string | null;
  paystack_subscription_code: string | null;
  created_at: string;
  // Cancellation fields
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  // Scheduled tier change fields
  scheduled_tier: string | null;
  scheduled_tier_effective_date: string | null;
  client_connector_id: number | null;
  category: ServiceCategory;
  // cp #843: the row's own id (the billing-start edit addresses it) and the
  // paid start a platform admin set; null means billing runs from created_at.
  id?: string | null;
  billing_start_utc?: string | null;
}

/** The earliest date a backdated start or a new discount may take (cp #843/#844). */
export interface BillingFloor {
  organisation_id: string;
  floor: string;
  last_billed_period_end: string | null;
}

export interface SubscriptionsListResponse {
  items: Subscription[];
  total: number;
}

export interface AddServiceSubscriptionRequest {
  service_name: string;
  tier: string;
  paystack_payment_token?: string;
  client_connector_id?: number;
}

export interface ServiceSubscriptionResponse {
  id: string;
  organisation_id: string;
  service_id: string;
  service_name: string;
  tier: string;
  status: string;
  client_connector_id: number | null;
  current_period_start: string | null;
  current_period_end: string | null;
  created_at: string;
}

/**
 * Tier-change response: the subscription row plus what the change cost.
 *
 * The CP tier route returns four extra keys (cp #574). They were being
 * serialised away server-side, so the portal showed a hardcoded success toast
 * and the customer was never told when the prorated difference could not be
 * charged and had been deferred to their next invoice.
 *
 * All are optional: an upgrade sets all of them, a scheduled downgrade
 * sets `change_type` and `message` only, and a no-op tier change sets
 * `message` alone. cp #707 added `proration_on_next_invoice`.
 */
export interface ServiceSubscriptionTierChangeResponse extends ServiceSubscriptionResponse {
  change_type?: "upgrade" | "downgrade_scheduled" | null;
  /** VAT-inclusive cents actually collected. Zero when nothing could be taken. */
  prorated_amount?: number | null;
  /**
   * VAT-inclusive cents computed. Differs from prorated_amount when nothing was
   * collected. Since cp #707 it is the ESTIMATE of the line the next monthly
   * invoice will carry (re-priced there with that month's discount).
   */
  prorated_amount_quoted?: number | null;
  /**
   * cp #707. `true` on every upgrade: nothing was charged at upgrade time and
   * the prorated difference is added to the next 1st-of-month invoice. `null`
   * (or absent, on a control plane older than #707) on a scheduled downgrade
   * or an unchanged tier. When true, `prorated_amount` is always 0 — that is
   * the design, NOT a failed charge, so it must not render as a warning.
   */
  proration_on_next_invoice?: boolean | null;
  message?: string | null;
}

// ============================================================================
// Types - Service Tiers (per-service pricing and allowances)
// ============================================================================

export type DiscountType =
  | "PROMOTIONAL"
  | "NGO"
  | "PARTNER"
  | "EARLY_ADOPTER"
  | "ENTERPRISE"
  | "CUSTOM";

export interface ServiceTier {
  id: string;
  service_id: string;
  name: string;
  display_name: string;
  sort_order: number;
  price_monthly_usd: number;
  price_monthly_zar: number;
  price_annual_usd: number;
  price_annual_zar: number;
  allowances: Record<string, number> | null;
  features: Record<string, boolean | string> | null;
  overage_pricing: Record<string, number> | null;
  contact_sales: boolean;
  is_active: boolean;
  sku_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceTiersListResponse {
  items: ServiceTier[];
  total: number;
  offset: number;
  limit: number;
}

export interface ServiceTierCreateRequest {
  service_id: string;
  name: string;
  display_name: string;
  sort_order?: number;
  price_monthly_usd?: number;
  price_monthly_zar?: number;
  price_annual_usd?: number;
  price_annual_zar?: number;
  allowances?: Record<string, number>;
  features?: Record<string, boolean | string>;
  overage_pricing?: Record<string, number>;
  contact_sales?: boolean;
  is_active?: boolean;
  sku_code?: string | null;
}

export interface ServiceTierUpdateRequest {
  display_name?: string;
  sort_order?: number;
  price_monthly_usd?: number;
  price_monthly_zar?: number;
  price_annual_usd?: number;
  price_annual_zar?: number;
  allowances?: Record<string, number>;
  features?: Record<string, boolean | string>;
  overage_pricing?: Record<string, number>;
  contact_sales?: boolean;
  is_active?: boolean;
  sku_code?: string | null;
}

// ============================================================================
// Types - Organisation Discounts
// ============================================================================

export interface OrganisationDiscount {
  id: string;
  organisation_id: string;
  service_id: string | null;
  service_feature_id: string | null;
  discount_percent: number;
  discount_type: DiscountType;
  valid_from: string;
  valid_to: string | null;
  reason: string | null;
  created_by: string;
  is_active: boolean;
  is_expired: boolean;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrganisationDiscountsListResponse {
  items: OrganisationDiscount[];
  total: number;
  offset: number;
  limit: number;
}

export interface OrganisationDiscountCreateRequest {
  organisation_id: string;
  discount_percent: number;
  discount_type: DiscountType;
  valid_from: string;
  valid_to?: string | null;
  service_id?: string | null;
  service_feature_id?: string | null;
  reason?: string;
  is_active?: boolean;
}

export interface OrganisationDiscountUpdateRequest {
  discount_percent?: number;
  discount_type?: DiscountType;
  valid_from?: string;
  valid_to?: string | null;
  service_id?: string | null;
  service_feature_id?: string | null;
  reason?: string;
  is_active?: boolean;
}

export interface DiscountSummary {
  organisation_id: string;
  active_count: number;
  expired_count: number;
  upcoming_count: number;
  total_count: number;
}

// ============================================================================
// Types - Platform Services (Service Definitions)
// ============================================================================

export interface ServicePricing {
  monthly_usd: number;
  monthly_zar: number;
  annual_usd: number;
  annual_zar: number;
  users: number; // -1 for unlimited
}

export interface PlatformService {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  pricing_model: "tier";
  category: ServiceCategory;
  has_free_tier: boolean;
  trial_days: number;
  is_active: boolean;
  is_launched: boolean;
  sort_order: number;
  pricing: {
    starter?: ServicePricing;
    professional?: ServicePricing;
    enterprise?: ServicePricing;
  } | null;
  allowances: Record<string, Record<string, number>> | null;
  overage_pricing: Record<string, Record<string, number>> | null;
  b2c_connector_type: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceCreateRequest {
  name: string;
  display_name: string;
  description?: string;
  pricing_model?: "tier";
  category?: ServiceCategory;
  has_free_tier?: boolean;
  trial_days?: number;
  is_active?: boolean;
  is_launched?: boolean;
  sort_order?: number;
  pricing?: Record<string, ServicePricing>;
  allowances?: Record<string, Record<string, number>>;
  overage_pricing?: Record<string, Record<string, number>>;
}

export interface ServiceUpdateRequest {
  display_name?: string;
  description?: string;
  pricing_model?: "tier";
  category?: ServiceCategory;
  has_free_tier?: boolean;
  trial_days?: number;
  is_active?: boolean;
  is_launched?: boolean;
  sort_order?: number;
  pricing?: Record<string, ServicePricing>;
  allowances?: Record<string, Record<string, number>>;
  overage_pricing?: Record<string, Record<string, number>>;
}

// ============================================================================
// Types - Service Features (SKU catalog, tier inclusions, assignments)
// ============================================================================

export interface ServiceFeature {
  id: string;
  service_id: string;
  feature_key: string;
  display_name: string;
  description: string | null;
  sku_code: string;
  price_monthly_zar: number;
  price_monthly_usd: number;
  is_base: boolean;
  b2c_licence_model_id: number | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ServiceFeatureListResponse {
  service_id: string;
  features: ServiceFeature[];
}

export interface ServiceFeatureCreateRequest {
  service_id: string;
  feature_key: string;
  display_name: string;
  description?: string;
  sku_code?: string;
  price_monthly_zar?: number;
  price_monthly_usd?: number;
  is_base?: boolean;
  b2c_licence_model_id?: number;
  is_active?: boolean;
  sort_order?: number;
}

export interface ServiceFeatureUpdateRequest {
  display_name?: string;
  description?: string;
  sku_code?: string;
  price_monthly_zar?: number;
  price_monthly_usd?: number;
  is_base?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

export interface ServiceTierFeature {
  id: string;
  service_tier_id: string;
  service_feature_id: string;
}

export interface ServiceFeatureAssignment {
  id: string;
  service_feature_id: string;
  organisation_id: string;
  service_subscription_id: string | null;
  service_item_subscription_id: string | null;
  quantity: number;
  price_monthly_zar: number;
  price_monthly_usd: number;
  status: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  feature_key?: string;
  display_name?: string;
}

// ============================================================================
// Types - Usage & Billing
// ============================================================================

export interface UsageSummary {
  organisation_id: string;
  period_start: string;
  period_end: string;
  total_cost: number;
  services: {
    service_name: string;
    metric_name: string;
    usage: number;
    cost: number;
  }[];
}

// ============================================================================
// Types - RBAC
// ============================================================================

export interface RBACPolicy {
  id: string;
  subject: string;
  organisation: string;
  resource: string;
  action: string;
  effect: string; // "allow" or "deny"
}

export interface RBACPoliciesListResponse {
  items: RBACPolicy[];
  total: number;
}

export interface RBACRole {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

export interface RBACRolesListResponse {
  items: RBACRole[];
  total: number;
}

// ============================================================================
// Types - Categories
// ============================================================================

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  organisation_id: string | null;
  parent_id: string | null;
  path: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  document_count: number;
  created_at: string;
  updated_at: string;
}

export interface CategoriesListResponse {
  items: Category[];
  total: number;
}

export interface CategoryCreateRequest {
  name: string;
  description?: string;
  organisation_id?: string;
  parent_id?: string;
}

export interface CategoryUpdateRequest {
  name?: string;
  description?: string;
  parent_id?: string | null;
  sort_order?: number;
}

// ============================================================================
// Types - Category Assignments
// ============================================================================

export interface CategoryAssignment {
  id: string;
  document_id: string;
  category_id: string;
  organisation_id: string;
  confidence: number | null;
  assigned_by: string;
  reasoning: string | null;
  for_review: boolean;
  assigned_at: string;
  reviewed: boolean;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  review_action: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
  category: Category | null;
}

// ============================================================================
// Types - Labels
// ============================================================================

export interface DocumentLabel {
  id: string;
  organisation_id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string | null;
  usage_count: number;
  created_at: string;
  created_by: string | null;
  deleted_at: string | null;
}

export interface DocumentLabelCreate {
  name: string;
  slug: string;
  organisation_id: string;
  description?: string;
  color?: string;
}

export interface DocumentLabelUpdate {
  name?: string;
  description?: string;
  color?: string;
}

// ============================================================================
// Types - Security / Audit
// ============================================================================

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  user_id: string;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id: string;
  ip_address: string;
  details: Record<string, unknown>;
}

export interface AuditLogResponse {
  items: AuditLogEntry[];
  total: number;
}

export interface SecuritySettings {
  require_mfa: boolean;
  allow_password_login: boolean;
  allow_sso_login: boolean;
  session_timeout_minutes: number;
  password_min_length: number;
  ip_whitelist_enabled: boolean;
  whitelisted_ips: string[];
}

export interface SecurityStats {
  active_sessions: number;
  failed_logins_24h: number;
  mfa_enabled_users: number;
  total_users: number;
  mfa_percentage: number;
  security_score: "Good" | "Fair" | "Poor";
}

// ============================================================================
// Types - Monitoring
// ============================================================================

export interface ServiceHealth {
  name: string;
  status: "healthy" | "degraded" | "down";
  latency_ms: number;
  uptime_percent: number;
  last_check: string;
}

export interface SystemMetrics {
  cpu_percent: number;
  memory_percent: number;
  disk_percent: number;
  request_rate: number;
  avg_response_time_ms: number;
  error_rate_percent: number;
  active_connections: number;
}

// ============================================================================
// Types - Platform Health
// ============================================================================

export interface ContainerAppHealth {
  name: string;
  cpu_percent: number | null;
  memory_percent: number | null;
  replicas: number | null;
  restart_count: number | null;
  status: "healthy" | "warning" | "critical" | "unknown";
}

export interface RedisHealth {
  connected: boolean;
  used_memory_human: string | null;
  connected_clients: number | null;
  queue_depths: Record<string, number>;
  total_pending_jobs: number;
}

export interface MssqlHealth {
  connected: boolean;
  active_connections: number | null;
  blocked_requests: number | null;
  latency_ms: number | null;
}

export interface PostgresHealth {
  connected: boolean;
  active_connections: number | null;
  max_connections: number | null;
  cache_hit_ratio: number | null;
  dead_tuples: number | null;
  long_running_queries: number | null;
}

export interface ServiceRegistryHealthItem {
  name: string;
  status: string;
  healthy: boolean;
}

export interface PlatformHealthDigest {
  timestamp: string;
  overall_status: "healthy" | "degraded" | "critical";
  container_apps: ContainerAppHealth[];
  redis: RedisHealth | null;
  mssql: MssqlHealth | null;
  postgres: PostgresHealth | null;
  services: ServiceRegistryHealthItem[];
}

export interface ContainerAppListResponse {
  items: ContainerAppHealth[];
  total: number;
}

// ============================================================================
// Types - DynDNS
// ============================================================================

export interface DynDnsDomain {
  id: string;
  domain_name: string;
  subdomain: string;
  hostname: string;
  record_type: string;
  ttl: number;
  current_ip: string | null;
  last_update_at_utc: string | null;
  update_count: number;
  is_active: boolean;
  created_at_utc: string;
}

export interface DynDnsDomainsListResponse {
  items: DynDnsDomain[];
  total: number;
  page: number;
  page_size: number;
}

export interface DynDnsDomainCreateRequest {
  domain_name: string;
  subdomain: string;
  record_type?: string;
  ttl?: number;
}

export interface DynDnsDomainUpdateRequest {
  ttl?: number;
  is_active?: boolean;
}

// Response from creating a domain - includes the API key (shown once!)
export interface DynDnsDomainCreateResponse extends DynDnsDomain {
  api_key: string;
}

// Response from regenerating an API key
export interface DynDnsApiKeyResponse {
  api_key: string;
  hostname: string;
  message: string;
}

// ============================================================================
// Types - Messaging
// ============================================================================

export interface ChannelConfig {
  id: string;
  organisation_id: string;
  organisation_name?: string;
  channel_type: string;
  name: string;
  is_active: boolean;
  is_default: boolean;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ChannelConfigsListResponse {
  items: ChannelConfig[];
  total: number;
}

/** Unified-inbox conversation thread (channel-agnostic). */
export interface MessagingConversation {
  id: string;
  organisation_id: string;
  /** External-system identifier for the customer (phone, email, etc) */
  customer_identifier: string;
  customer_id?: string | null;
  /** "email" | "whatsapp" | "teams" | "sms" */
  channel: string;
  external_thread_id?: string | null;
  /** "open" | "pending" | "resolved" | "closed" */
  status: string;
  subject?: string | null;
  sentiment_score?: number | null;
  last_intent?: string | null;
  assigned_to?: string | null;
  ai_handling: boolean;
  message_count: number;
  unread_count: number;
  last_message_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface MessagingConversationsListResponse {
  items: MessagingConversation[];
  total: number;
  limit: number;
  offset: number;
}

/** Single message inside a conversation thread. */
export interface MessagingMessage {
  id: string;
  conversation_id: string;
  /** "inbound" | "outbound" */
  direction: string;
  channel: string;
  content?: string | null;
  content_type: string;
  sender_identifier?: string | null;
  sender_name?: string | null;
  external_message_id?: string | null;
  sentiment?: Record<string, unknown> | null;
  intent?: Record<string, unknown> | null;
  entities?: Array<Record<string, unknown>> | null;
  ai_generated: boolean;
  ai_reviewed: boolean;
  /** "sent" | "delivered" | "read" | "failed" */
  status?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  error_message?: string | null;
  attachments?: Array<Record<string, unknown>> | null;
  sent_at: string;
}

export interface MessagingMessagesListResponse {
  items: MessagingMessage[];
  total: number;
  limit: number;
  offset: number;
}

/** Live phone-number status returned by Meta GET /{phone_number_id}. */
export interface WhatsAppPhoneStatus {
  id: string;
  /** CONNECTED | OFFLINE | FLAGGED | RESTRICTED */
  status?: string;
  /** VERIFIED | EXPIRED | NOT_VERIFIED */
  code_verification_status?: string;
  /** GREEN | YELLOW | RED | UNKNOWN */
  quality_rating?: string;
  /** Display name approved by Meta. */
  verified_name?: string;
  /** Pretty-printed E.164. */
  display_phone_number?: string;
}

export interface ChannelConfigCreateRequest {
  channel_type: string;
  name: string;
  config: Record<string, string>;
  is_default?: boolean;
}

/**
 * A parked inbound message (ME26). Each row is a customer's WhatsApp message
 * that is permanently lost unless an operator replays it — the webhook already
 * ACKed 200 and forfeited the provider's redelivery.
 */
/** The API's own maximum; asking for less would hide lost messages. */
export const DEAD_LETTER_PAGE_LIMIT = 500;

export interface InboundDeadLetter {
  id: string;
  organisation_id: string;
  channel: string;
  external_message_id: string | null;
  error: string | null;
  resolved: boolean;
  created_at_utc: string | null;
}

export interface InboundDeadLetterDetail extends InboundDeadLetter {
  /** The raw parked payload — the only surviving copy of the message. */
  payload: unknown;
}

export interface InboundDeadLetterActionResult {
  id: string;
  replayed: boolean;
  resolved: boolean;
  detail: string;
}

/**
 * A reporting schedule (`cp01.Schedule`) whose `Config.reportSlug` no longer
 * matches any report DP knows about for that organisation — the report was
 * deleted or renamed, but nothing stops the schedule from firing forever
 * against a slug that's gone. Failing silently: no alert, no owner told.
 */
export interface OrphanedSchedule {
  scheduleId: string;
  organisationId: string;
  organisationName: string;
  displayName: string | null;
  reportSlug: string;
  isActive: boolean;
  lastRunStatus: string | null;
  lastRunError: string | null;
  createdAtUtc: string;
}

export interface OrphanedSchedulesResponse {
  orphaned: OrphanedSchedule[];
  /** Orgs excluded from this scan because DP's slug lookup failed for them —
   * not an error, but an incomplete result; retry later to pick them up. */
  skippedOrganisationIds: string[];
}

export interface MessagingHealthResponse {
  status: string;
  service: string;
  components: Record<string, string>;
}

export interface TemplateVersion {
  id: string;
  channel: string;
  status: string;
  external_id: string | null;
  rejection_reason: string | null;
  content: Record<string, unknown> | null;
}

export interface MessageTemplate {
  id: string;
  organisation_id: string;
  name: string;
  description: string | null;
  category: string | null;
  is_active: boolean;
  variables: Record<string, unknown>[] | null;
  versions: TemplateVersion[];
  created_at: string | null;
  updated_at: string | null;
}

export interface TemplateSyncResult {
  synced: number;
  created: number;
  updated: number;
  errors: string[];
}

export interface TemplateSubmitResult {
  success: boolean;
  meta_response: Record<string, unknown>;
}

export interface DynDnsUpdateLog {
  id: string;
  hostname: string;
  old_ip: string | null;
  new_ip: string;
  source_ip: string;
  user_agent: string | null;
  status: string;
  message: string | null;
  created_at_utc: string;
}

export interface DynDnsUpdateLogsListResponse {
  items: DynDnsUpdateLog[];
  total: number;
  page: number;
  page_size: number;
}

// ============================================================================
// Types - VPN (WireGuard)
// ============================================================================

export type VpnPeerType = "internal" | "customer";

export interface VpnPeer {
  id: string;
  organisation_id: string;
  name: string;
  public_key: string;
  allowed_ips: string;
  status: string;
  tunnel_mode: string;
  peer_type: VpnPeerType;
  created_at: string;
  last_handshake_at: string | null;
  endpoint: string | null;
  latest_handshake: number | null;
  transfer_rx: number | null;
  transfer_tx: number | null;
  is_active: boolean;
  in_wg: boolean;
}

export interface VpnPeersListResponse {
  peers: VpnPeer[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface VpnPeerCreateRequest {
  name: string;
  tunnel_mode: "full" | "split";
  peer_type?: VpnPeerType;
}

export interface VpnPeerConfigResponse {
  config: string;
  filename: string;
}

export interface VpnPeerStatusItem {
  public_key: string;
  endpoint: string | null;
  allowed_ips: string;
  latest_handshake: number;
  transfer_rx: number;
  transfer_tx: number;
  is_active: boolean;
}

export interface VpnStatusResponse {
  interface: string;
  public_key: string;
  listen_port: number;
  total_peers: number;
  active_peers: number;
  peers: VpnPeerStatusItem[];
}

/**
 * One IPsec (strongSwan) connection as the wireguard service reports it:
 * the stored row plus live SA state from `swanctl --list-sas`. Read-only —
 * connections are created with the wireguard service CLI. Never carries the
 * pre-shared key.
 *
 * `connected` is `null` when the live state could not be read (see
 * `IpsecConnectionsListResponse.live_state_error`) — "unknown", never "down".
 */
export interface IpsecConnectionStatus {
  id: string;
  name: string;
  organisation_id: string;
  status: "active" | "disabled";
  peer_public_ip: string;
  alias_ip: string;
  remote_db_host: string;
  remote_db_port: number;
  ike_sa_state: string | null;
  child_sa_state: string | null;
  connected: boolean | null;
  established_seconds: number | null;
  bytes_in: number | null;
  bytes_out: number | null;
  created_at: string;
}

export interface IpsecConnectionsListResponse {
  connections: IpsecConnectionStatus[];
  /** Set when swanctl could not be read; every `connected` is then null. */
  live_state_error: string | null;
}

// ============================================================================
// Types - Timezones
// ============================================================================

export interface TimezoneItem {
  value: string;
  label: string;
  region: string;
  utc_offset: string;
  /** True only on the principal zone of a `country`-filtered response. */
  is_primary: boolean;
}

/**
 * One row of `cp01.Country` as `GET /api/v1/countries` serves it.
 *
 * `vat_rate` is the country's own rate and is 0 for every non-supplier country;
 * it is NOT the rate an organisation is billed at, which the control plane
 * resolves from the treatment (a STANDARD override on a foreign org is charged
 * the supplier's rate). Use it for picker hints, never to compute money.
 */
export interface CountryItem {
  code: string;
  name: string;
  is_active: boolean;
  vat_treatment: VatTreatment;
  vat_rate: number;
  default_currency: string;
  sort_order: number;
}

export interface CountryListResponse {
  items: CountryItem[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    page: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface TimezoneListResponse {
  items: TimezoneItem[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    page: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

// ============================================================================
// Types - Platform Settings
// ============================================================================

export interface PlatformSettings {
  platform_name: string;
  platform_url: string;
  support_email: string;
  default_timezone: string;
  description: string;
}

// ============================================================================
// Types - Organisation Members
// ============================================================================

export interface OrganisationMember {
  id: string;
  email: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  is_owner: boolean;
  is_active: boolean;
  last_login_at: string | null;
  locked_until: string | null;
  failed_login_attempts: number;
  created_at: string;
}

export interface InvitationRoleOption {
  value: string;
  label: string;
}

export interface InvitationRolesResponse {
  roles: InvitationRoleOption[];
  // Backend-owned default role to preselect (source of truth: invite schema).
  default: string;
}

/** Upper bound per request — must track MAX_INVITE_EMAILS in the backend schema. */
export const MAX_INVITE_EMAILS = 50;

/** Upper bound per request — must track MAX_INVITE_GROUPS in the backend schema. */
export const MAX_INVITE_GROUPS = 10;

export interface InviteMemberRequest {
  emails: string[];
  role: string;
  /**
   * Groups every invitee joins when they accept. Unlike a bad email, an invalid
   * group id rejects the whole request rather than failing one row.
   */
  group_ids?: string[];
}

export interface InvitationGroupSummary {
  id: string;
  name: string;
}

export interface InviteMemberResultItem {
  email: string;
  status: "invited" | "updated" | "skipped" | "failed";
  error: string | null;
  invitation: Invitation | null;
}

export interface InviteMemberSummary {
  /** Raw emails submitted, before duplicates were collapsed. */
  requested: number;
  invited: number;
  /**
   * Already had a pending invitation whose GROUPS were reconciled to this
   * request. No new invitation, no second email — but something DID change.
   */
  updated: number;
  skipped: number;
  failed: number;
}

export interface InviteMemberResponse {
  results: InviteMemberResultItem[];
  summary: InviteMemberSummary;
}

export interface Invitation {
  id: string;
  organisation_id: string;
  email: string;
  role: string;
  status: "pending" | "accepted" | "expired" | "cancelled";
  invited_by: string | null;
  message: string | null;
  expires_at: string;
  created_at: string;
  email_sent_at: string | null;
  resend_count: number;
  accepted_at: string | null;
  cancelled_at: string | null;
  inviter_name: string | null;
  inviter_email: string | null;
  organisation_name: string | null;
  is_expired: boolean;
  /** Groups this invitee joins on acceptance. */
  groups: InvitationGroupSummary[];
}

export interface InvitationListResponse {
  items: Invitation[];
  total: number;
}

export interface AddExistingUserRequest {
  email: string;
  role: "member" | "admin" | "owner";
}

export interface AddExistingUserResponse {
  message: string;
  user: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  organisation: {
    id: string;
    name: string;
  };
  role: string;
}

// ============================================================================
// Types - Organisation Settings
// ============================================================================

export interface OrganisationSettings {
  id: string;
  name: string;
  slug: string;
  description: string;
  timezone: string;
  country_code?: string;
  billing_currency?: string;
  billing_email?: string | null;
  // Legal billing entity for invoices (e.g. "Larasoft (Pty) Ltd") — the Xero
  // contact uses this, falling back to the org name when blank.
  billing_entity_name?: string | null;
  /**
   * Which contact in Xero this org's invoices belong to, matched BY NAME.
   * Authoritative over `billing_entity_name` at invoice issue when set.
   */
  xero_contact_name?: string | null;
  vat_number?: string | null;
  /** Org-level override of the country's VAT treatment; null = inherit. */
  vat_treatment?: VatTreatment | null;
  // Billing address — the "Bill To" block synced to the Xero contact.
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * `client_connector_id` query fragment naming a per-connector subscription row
 * (dp #2284), or nothing for a platform service. `separator` is "?" when it is
 * the first query parameter, "&" otherwise.
 */
function connectorQuery(clientConnectorId: number | null, separator: "?" | "&"): string {
  if (clientConnectorId === null || clientConnectorId === undefined) return "";
  return `${separator}client_connector_id=${encodeURIComponent(String(clientConnectorId))}`;
}

export const platformApi = {
  // ============================================================================
  // Users
  // ============================================================================

  listUsers: (
    token: string,
    params?: { offset?: number; limit?: number; search?: string; sort_by?: string; sort_order?: "asc" | "desc" },
  ) => {
    const searchParams = new URLSearchParams({
      offset: String(params?.offset || 0),
      limit: String(params?.limit || 25),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<UsersListResponse>(`/api/v1/users?${searchParams}`, {
      token,
    });
  },

  getUser: (token: string, userId: string) =>
    portalFetch<PlatformUser>(`/api/v1/users/${userId}`, { token }),

  createUser: (
    token: string,
    data: {
      email: string;
      username: string;
      password: string;
      first_name?: string;
      last_name?: string;
    },
  ) =>
    portalFetch<PlatformUser>("/api/v1/users/register", {
      token,
      method: "POST",
      body: data,
    }),

  updateUser: (
    token: string,
    userId: string,
    data: {
      first_name?: string;
      last_name?: string;
      email?: string;
      is_active?: boolean;
      is_admin?: boolean;
    },
  ) =>
    portalFetch<PlatformUser>(`/api/v1/users/${userId}`, {
      token,
      method: "PATCH",
      body: data,
    }),

  deleteUser: (token: string, userId: string) =>
    portalFetch(`/api/v1/users/${userId}`, {
      token,
      method: "DELETE",
    }),

  unlockUser: (token: string, userId: string) =>
    portalFetch<{ message: string }>(`/api/v1/users/${userId}/unlock`, {
      token,
      method: "POST",
    }),

  sendPasswordReset: (
    token: string,
    organisationId: string,
    userId: string,
  ) =>
    portalFetch<{ message: string }>(
      `/api/v1/organisations/${organisationId}/users/${userId}/send-password-reset`,
      { token, method: "POST" },
    ),

  getMyProfile: (token: string) =>
    portalFetch<PlatformUser>("/api/v1/users/me", { token }),

  /** Roaming, account-scoped display preferences — see decalPreference.ts.
   * decals_enabled is null when the user has never set it explicitly. */
  getMyPreferences: (token: string) =>
    portalFetch<{ decals_enabled: boolean | null }>(
      "/api/v1/users/me/preferences",
      { token },
    ),

  updateMyPreferences: (token: string, data: { decals_enabled: boolean | null }) =>
    portalFetch<{ decals_enabled: boolean | null }>(
      "/api/v1/users/me/preferences",
      { token, method: "PUT", body: data },
    ),

  updateMyProfile: (
    token: string,
    data: { first_name?: string; last_name?: string },
  ) =>
    portalFetch<PlatformUser>("/api/v1/users/me", {
      token,
      method: "PATCH",
      body: data,
    }),

  changePassword: (
    token: string,
    data: { current_password: string; new_password: string },
  ) =>
    portalFetch<{ message: string }>("/api/v1/auth/change-password", {
      token,
      method: "POST",
      body: data,
    }),

  // ============================================================================
  // Organisations
  // ============================================================================

  listOrganisations: (
    token: string,
    params?: { offset?: number; limit?: number; search?: string; sort_by?: string; sort_order?: "asc" | "desc" },
  ) => {
    const searchParams = new URLSearchParams({
      offset: String(params?.offset || 0),
      limit: String(params?.limit || 25),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<OrganisationsListResponse>(
      `/api/v1/organisations/?${searchParams}`,
      {
        token,
      },
    );
  },

  getOrganisation: (token: string, orgId: string) =>
    portalFetch<Organisation>(`/api/v1/organisations/${orgId}`, { token }),

  createOrganisation: (
    token: string,
    data: {
      name: string;
      slug: string;
      time_zone?: string;
      settings?: Record<string, unknown>;
      /**
       * Seeds `BillingCurrency` from `cp01.Country.DefaultCurrency` when
       * `billing_currency` is omitted — the one moment country is allowed to
       * decide currency. Admin-only: the control plane 403s a non-admin who
       * sends either field.
       */
      country_code?: string;
      billing_currency?: string;
    },
  ) =>
    portalFetch<Organisation>("/api/v1/organisations/", {
      token,
      method: "POST",
      body: data,
    }),

  updateOrganisation: (
    token: string,
    orgId: string,
    data: {
      name?: string;
      time_zone?: string;
      settings?: Record<string, unknown>;
      /**
       * Both are admin-only and both move money: `billing_currency` selects the
       * price branch, `country_code` decides VAT. A PATCH carrying
       * `country_code` no longer rewrites the currency — that auto-derive was
       * the defect in web #970 — so omitting `billing_currency` leaves it
       * alone. Callers send it explicitly anyway, to state the intent.
       */
      country_code?: string;
      billing_currency?: string;
    },
  ) =>
    portalFetch<Organisation>(`/api/v1/organisations/${orgId}`, {
      token,
      method: "PATCH",
      body: data,
    }),

  deleteOrganisation: (token: string, orgId: string) =>
    portalFetch(`/api/v1/organisations/${orgId}`, {
      token,
      method: "DELETE",
    }),

  getOrganisationUsers: async (
    token: string,
    orgId: string,
  ): Promise<OrganisationUser[]> => {
    const data = await portalFetch<{ items: OrganisationUser[]; total: number }>(
      `/api/v1/organisations/${orgId}/users?limit=100`,
      { token },
    );
    return data.items ?? [];
  },

  // ============================================================================
  // External API Keys
  // ============================================================================

  listAPIKeys: (
    token: string,
    organisationId: string,
    params?: { offset?: number; limit?: number; search?: string; sort_by?: string; sort_order?: "asc" | "desc" },
  ) => {
    const searchParams = new URLSearchParams({
      offset: String(params?.offset || 0),
      limit: String(params?.limit || 25),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<ExternalAPIKeyListResponse>(
      `/api/v1/organisations/${organisationId}/api-keys?${searchParams}`,
      { token },
    );
  },

  createAPIKey: (
    token: string,
    organisationId: string,
    data: ExternalAPIKeyCreateRequest,
  ) =>
    portalFetch<ExternalAPIKeyCreateResponse>(
      `/api/v1/organisations/${organisationId}/external-api-keys`,
      {
        token,
        method: "POST",
        body: data,
      },
    ),

  revokeAPIKey: (token: string, organisationId: string, keyId: string) =>
    portalFetch(
      `/api/v1/organisations/${organisationId}/external-api-keys/${keyId}`,
      {
        token,
        method: "DELETE",
      },
    ),

  // ============================================================================
  // Subscriptions
  // ============================================================================

  listSubscriptions: (
    token: string,
    params?: {
      status?: string;
      category?: string;
      offset?: number;
      limit?: number;
      search?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams({
      offset: String(params?.offset || 0),
      limit: String(params?.limit || 25),
    });
    if (params?.status) searchParams.append("status", params.status);
    if (params?.category) searchParams.append("category", params.category);
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<SubscriptionsListResponse>(
      `/api/v1/subscriptions/admin/services/all?${searchParams}`,
      {
        token,
      },
    );
  },

  /** Platform admin only. `floor` is a YYYY-MM-DD date for a date input's `min`. */
  getBillingFloor: (token: string, organisationId: string) =>
    portalFetch<BillingFloor>(
      `/api/v1/billing/organisations/${organisationId}/billing-floor`,
      { token },
    ),

  /**
   * Platform admin only (cp #843): when the paid service began, if not at the
   * row's creation. `null` clears it. The CP answers 409 when the move would
   * re-price a billed invoice; the message says which and what to pick.
   */
  setSubscriptionBillingStart: (
    token: string,
    subscriptionId: string,
    billingStartUtc: string | null,
  ) =>
    portalFetch<{
      id: string;
      billing_start_utc: string | null;
      created_at: string;
    }>(`/api/v1/billing/subscriptions/${subscriptionId}/billing-start`, {
      token,
      method: "PUT",
      body: { billing_start_utc: billingStartUtc },
    }),

  getOrgServiceSubscriptions: (token: string, organisationId: string) =>
    portalFetch<Subscription[]>(
      `/api/v1/subscriptions/organisation/${organisationId}/services`,
      { token },
    ),

  addServiceSubscription: (
    token: string,
    organisationId: string,
    data: AddServiceSubscriptionRequest,
  ) =>
    portalFetch<ServiceSubscriptionResponse>(
      `/api/v1/subscriptions/organisation/${organisationId}/services`,
      {
        token,
        method: "POST",
        body: data,
      },
    ),

  /**
   * `clientConnectorId` names the ROW for a per-connector service (one row per
   * connector instance). Without it the CP refuses (422) when the org holds more
   * than one row, rather than acting on whichever is newest (dp #2284: a Cancel
   * on a retired Supernova row once cancelled the live Google Ads connector).
   * Pass the clicked row's `client_connector_id`; null for platform services.
   */
  upgradeServiceTier: (
    token: string,
    organisationId: string,
    serviceName: string,
    tier: string,
    clientConnectorId: number | null = null,
  ) =>
    portalFetch<ServiceSubscriptionTierChangeResponse>(
      `/api/v1/subscriptions/organisation/${organisationId}/services/${serviceName}/tier` +
        connectorQuery(clientConnectorId, "?"),
      {
        token,
        method: "PUT",
        body: { tier },
      },
    ),

  /**
   * `cancelPair` cancels BOTH halves of the Workflows <-> MyApp Professional
   * pairing (item 6). Without it the backend 422s a lone cancel that would
   * strand the other half — the portal must ask, not assume.
   */
  cancelServiceSubscription: (
    token: string,
    organisationId: string,
    serviceName: string,
    immediately: boolean = false,
    cancelPair: boolean = false,
    clientConnectorId: number | null = null,
  ) =>
    portalFetch<ServiceSubscriptionResponse>(
      `/api/v1/subscriptions/organisation/${organisationId}/services/${serviceName}` +
        `?immediately=${immediately}&cancel_pair=${cancelPair}` +
        connectorQuery(clientConnectorId, "&"),
      {
        token,
        method: "DELETE",
      },
    ),

  reactivateServiceSubscription: (
    token: string,
    organisationId: string,
    serviceName: string,
    clientConnectorId: number | null = null,
  ) =>
    portalFetch<ServiceSubscriptionResponse>(
      `/api/v1/subscriptions/organisation/${organisationId}/services/${serviceName}/reactivate` +
        connectorQuery(clientConnectorId, "?"),
      {
        token,
        method: "POST",
      },
    ),

  cancelScheduledTierChange: (
    token: string,
    organisationId: string,
    serviceName: string,
    clientConnectorId: number | null = null,
  ) =>
    portalFetch<ServiceSubscriptionResponse>(
      `/api/v1/subscriptions/organisation/${organisationId}/services/${serviceName}/scheduled-tier` +
        connectorQuery(clientConnectorId, "?"),
      {
        token,
        method: "DELETE",
      },
    ),

  // ============================================================================
  // Usage & Billing
  // ============================================================================

  getUsageSummary: (
    token: string,
    params?: { start_date?: string; end_date?: string },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    const query = searchParams.toString();
    return portalFetch<UsageSummary>(
      `/api/v1/billing/usage${query ? `?${query}` : ""}`,
      { token },
    );
  },

  // ============================================================================
  // RBAC
  // ============================================================================

  listPolicies: (
    token: string,
    params?: {
      offset?: number;
      limit?: number;
      search?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams({
      offset: String(params?.offset || 0),
      limit: String(params?.limit || 25),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<RBACPoliciesListResponse>(
      `/api/v1/rbac/policies?${searchParams}`,
      {
        token,
      },
    );
  },

  listRoles: (
    token: string,
    params?: {
      offset?: number;
      limit?: number;
      search?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams({
      offset: String(params?.offset || 0),
      limit: String(params?.limit || 25),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<RBACRolesListResponse>(
      `/api/v1/rbac/roles?${searchParams}`,
      { token },
    );
  },

  /**
   * Fetch the current user's feature-level permissions for a specific org.
   * Returns a map of feature names to action booleans, driven by backend Casbin RBAC.
   */
  getMyPermissions: (token: string) => {
    return portalFetch<{
      permissions: Record<string, Record<string, boolean>>;
      roles: string[];
      bypasses_groups: boolean;
      /** Larasoft platform staff — distinct from bypasses_groups (which is also
       *  true for org_owner/admin). Used to exempt platform admins from
       *  org-level guardrails such as the org-admin daily schedule-frequency cap. */
      is_platform_admin?: boolean;
    }>(`/api/v1/rbac/my-permissions`, {
      token,
    });
  },

  // ============================================================================
  // Categories
  // ============================================================================

  listCategories: async (
    token: string,
    params?: {
      offset?: number;
      limit?: number;
      search?: string;
      organisation_id?: string;
    },
  ): Promise<CategoriesListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.organisation_id) searchParams.set("organisation_id", params.organisation_id);
    if (params?.offset) searchParams.set("offset", String(params.offset));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.search) searchParams.set("search", params.search);
    const qs = searchParams.toString();
    return portalFetch<CategoriesListResponse>(
      `/api/v1/categories${qs ? `?${qs}` : ""}`,
      { token },
    );
  },

  createCategory: (token: string, data: CategoryCreateRequest) =>
    portalFetch<Category>(`/api/v1/documents/categories/`, {
      token,
      method: "POST",
      body: data,
    }),

  updateCategory: (
    token: string,
    categoryId: string,
    data: CategoryUpdateRequest,
  ) =>
    portalFetch<Category>(
      `/api/v1/documents/categories/${categoryId}`,
      {
        token,
        method: "PUT",
        body: data,
      },
    ),

  deleteCategory: (token: string, categoryId: string) =>
    portalFetch(
      `/api/v1/documents/categories/${categoryId}`,
      {
        token,
        method: "DELETE",
      },
    ),

  assignCategoryToDocument: (
    token: string,
    documentId: string,
    data: {
      document_id: string;
      category_id: string;
      organisation_id: string;
      assigned_by?: string;
      confidence?: number;
    },
  ) =>
    portalFetch(`/api/v1/categories/documents/${documentId}/categories`, {
      token,
      method: "POST",
      body: data,
    }),

  getDocumentCategoryAssignments: (token: string, documentId: string) =>
    portalFetch<CategoryAssignment[]>(
      `/api/v1/categories/documents/${documentId}/categories`,
      { token },
    ),

  deleteCategoryAssignment: (
    token: string,
    _documentId: string,
    assignmentId: string,
  ) =>
    portalFetch(
      `/api/v1/categories/assignments/${assignmentId}`,
      { token, method: "DELETE" },
    ),

  // ============================================================================
  // Labels
  // ============================================================================

  listLabels: async (
    token: string,
    params: {
      organisation_id: string;
      include_deleted?: boolean;
    },
  ): Promise<DocumentLabel[]> => {
    const searchParams = new URLSearchParams();
    searchParams.set("organisation_id", params.organisation_id);
    if (params.include_deleted) searchParams.set("include_deleted", "true");
    return portalFetch<DocumentLabel[]>(
      `/api/v1/documents/labels?${searchParams}`,
      { token },
    );
  },

  createLabel: (token: string, data: DocumentLabelCreate) =>
    portalFetch<DocumentLabel>(`/api/v1/documents/labels`, {
      token,
      method: "POST",
      body: data,
    }),

  updateLabel: (
    token: string,
    labelId: string,
    organisationId: string,
    data: DocumentLabelUpdate,
  ) =>
    portalFetch<DocumentLabel>(
      `/api/v1/documents/labels/${labelId}?organisation_id=${organisationId}`,
      {
        token,
        method: "PUT",
        body: data,
      },
    ),

  deleteLabel: (token: string, labelId: string, organisationId: string) =>
    portalFetch(
      `/api/v1/documents/labels/${labelId}?organisation_id=${organisationId}`,
      {
        token,
        method: "DELETE",
      },
    ),

  getDocumentWithLabels: (
    token: string,
    documentId: string,
    organisationId: string,
  ) =>
    portalFetch<{ labels: DocumentLabel[] }>(
      `/api/v1/documents/${documentId}?organisation_id=${organisationId}`,
      { token },
    ),

  updateDocumentLabels: (
    token: string,
    documentId: string,
    organisationId: string,
    labelIds: string[],
  ) =>
    portalFetch(
      `/api/v1/documents/${documentId}?organisation_id=${organisationId}`,
      {
        token,
        method: "PUT",
        body: { label_ids: labelIds },
      },
    ),

  // ============================================================================
  // Security / Audit
  // ============================================================================

  getAuditLogs: (
    token: string,
    params?: { event_type?: string; skip?: number; limit?: number },
  ) => {
    const searchParams = new URLSearchParams({
      skip: String(params?.skip || 0),
      limit: String(params?.limit || 100),
    });
    if (params?.event_type)
      searchParams.append("event_type", params.event_type);
    return portalFetch<AuditLogResponse>(
      `/api/v1/security/audit-logs/?${searchParams}`,
      { token },
    );
  },

  getSecuritySettings: (token: string) =>
    portalFetch<SecuritySettings>("/api/v1/security/settings/", { token }),

  getSecurityStats: (token: string) =>
    portalFetch<SecurityStats>("/api/v1/security/stats/", { token }),

  updateSecuritySettings: (token: string, data: Partial<SecuritySettings>) =>
    portalFetch<SecuritySettings>("/api/v1/security/settings/", {
      token,
      method: "PATCH",
      body: data,
    }),

  // ============================================================================
  // Monitoring
  // ============================================================================

  getServiceHealth: (token: string) =>
    portalFetch<{ services: ServiceHealth[] }>("/api/v1/monitoring/health/", {
      token,
    }),

  getSystemMetrics: (token: string) =>
    portalFetch<SystemMetrics>("/api/v1/monitoring/metrics/", { token }),

  getPlatformHealth: (token: string) =>
    portalFetch<PlatformHealthDigest>("/api/v1/monitoring/platform-health/", {
      token,
    }),

  getContainerApps: (
    token: string,
    params?: {
      offset?: number;
      limit?: number;
      search?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams({
      offset: String(params?.offset || 0),
      limit: String(params?.limit || 25),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<ContainerAppListResponse>(
      `/api/v1/monitoring/container-apps/?${searchParams}`,
      { token },
    );
  },

  refreshPlatformHealth: (token: string) =>
    portalFetch<PlatformHealthDigest>("/api/v1/monitoring/refresh", {
      token,
      method: "POST",
    }),

  // ============================================================================
  // Platform Settings
  // ============================================================================

  getPlatformSettings: (token: string) =>
    portalFetch<PlatformSettings>("/api/v1/platform/settings/", { token }),

  updatePlatformSettings: (token: string, data: Partial<PlatformSettings>) =>
    portalFetch<PlatformSettings>("/api/v1/platform/settings/", {
      token,
      method: "PATCH",
      body: data,
    }),

  // ============================================================================
  // Orphaned reporting schedules
  // ============================================================================
  // A schedule's Config.reportSlug can outlive the report it points at —
  // deleting/renaming a report in DP never touches CP's schedule rows, so it
  // keeps firing forever against a slug that no longer exists. No "repoint to
  // another report" action exists here by design; the admin recreates the
  // schedule by hand on the replacement report's own page.

  listOrphanedSchedules: (token: string) =>
    portalFetch<OrphanedSchedulesResponse>("/api/v1/platform/schedules/orphaned", {
      token,
    }),

  deactivateSchedule: (token: string, scheduleId: string) =>
    portalFetch<void>(`/api/v1/platform/schedules/${scheduleId}/deactivate`, {
      token,
      method: "POST",
    }),

  deleteSchedule: (token: string, scheduleId: string) =>
    portalFetch<void>(`/api/v1/platform/schedules/${scheduleId}`, {
      token,
      method: "DELETE",
    }),

  // ============================================================================
  // Platform Services (Service Definitions)
  // ============================================================================

  listPlatformServices: (token: string, includeInactive: boolean = false) =>
    portalFetch<PlatformService[]>(
      `/api/v1/platform/services/?include_inactive=${includeInactive}`,
      { token },
    ),

  listPlatformServicesPaginated: (
    token: string,
    params?: {
      include_inactive?: boolean;
      search?: string;
      category?: string;
      offset?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.include_inactive) searchParams.append("include_inactive", "true");
    if (params?.search) searchParams.append("search", params.search);
    if (params?.category) searchParams.append("category", params.category);
    searchParams.append("offset", String(params?.offset || 0));
    searchParams.append("limit", String(params?.limit || 25));
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<{ items: PlatformService[]; total: number; offset: number; limit: number }>(
      `/api/v1/platform/services/admin?${searchParams}`,
      { token },
    );
  },

  getPlatformService: (token: string, serviceId: string) =>
    portalFetch<PlatformService>(`/api/v1/platform/services/${serviceId}`, {
      token,
    }),

  createPlatformService: (token: string, data: ServiceCreateRequest) =>
    portalFetch<PlatformService>("/api/v1/platform/services/", {
      token,
      method: "POST",
      body: data,
    }),

  updatePlatformService: (
    token: string,
    serviceId: string,
    data: ServiceUpdateRequest,
  ) =>
    portalFetch<PlatformService>(`/api/v1/platform/services/${serviceId}`, {
      token,
      method: "PUT",
      body: data,
    }),

  deletePlatformService: (token: string, serviceId: string) =>
    portalFetch(`/api/v1/platform/services/${serviceId}`, {
      token,
      method: "DELETE",
    }),

  seedPlatformServices: (token: string, force: boolean = false) =>
    portalFetch<PlatformService[]>("/api/v1/platform/services/seed", {
      token,
      method: "POST",
      body: { force },
    }),

  // ============================================================================
  // Organisation Members
  // ============================================================================

  listOrganisationMembers: (
    token: string,
    organisationId: string,
    params?: {
      search?: string;
      offset?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    searchParams.append("offset", String(params?.offset || 0));
    searchParams.append("limit", String(params?.limit || 25));
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<{ items: OrganisationMember[]; total: number }>(
      `/api/v1/organisations/${organisationId}/users?${searchParams}`,
      { token },
    );
  },

  getInvitationRoles: (token: string) =>
    portalFetch<InvitationRolesResponse>(`/api/v1/invitations/roles`, { token }),

  inviteMember: (
    token: string,
    organisationId: string,
    data: InviteMemberRequest,
  ) =>
    portalFetch<InviteMemberResponse>(
      `/api/v1/organisations/${organisationId}/invitations`,
      {
        token,
        method: "POST",
        body: data,
      },
    ),

  listInvitations: (
    token: string,
    organisationId: string,
    params?: { status?: string },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append("status", params.status);
    const query = searchParams.toString() ? `?${searchParams}` : "";
    return portalFetch<InvitationListResponse>(
      `/api/v1/organisations/${organisationId}/invitations${query}`,
      { token },
    );
  },

  resendInvitation: (
    token: string,
    organisationId: string,
    invitationId: string,
  ) =>
    portalFetch<{ success: boolean; message: string }>(
      `/api/v1/organisations/${organisationId}/invitations/${invitationId}/resend`,
      { token, method: "POST" },
    ),

  cancelInvitation: (
    token: string,
    organisationId: string,
    invitationId: string,
  ) =>
    portalFetch<{ success: boolean; message: string }>(
      `/api/v1/organisations/${organisationId}/invitations/${invitationId}`,
      { token, method: "DELETE" },
    ),

  updateMemberRole: (
    token: string,
    organisationId: string,
    memberId: string,
    role: string,
  ) =>
    portalFetch<OrganisationMember>(
      `/api/v1/organisations/${organisationId}/users/${memberId}`,
      {
        token,
        method: "PATCH",
        body: { role },
      },
    ),

  removeMember: (token: string, organisationId: string, memberId: string) =>
    portalFetch(`/api/v1/organisations/${organisationId}/users/${memberId}`, {
      token,
      method: "DELETE",
    }),

  /**
   * Add an existing platform user to an organisation (Platform Admin only).
   * This does NOT send an invitation email - the user is added directly.
   */
  addExistingUserToOrganisation: (
    token: string,
    organisationId: string,
    data: AddExistingUserRequest,
  ) =>
    portalFetch<AddExistingUserResponse>(
      `/api/v1/organisations/${organisationId}/users/add-existing`,
      {
        token,
        method: "POST",
        body: data,
      },
    ),

  // ============================================================================
  // Organisation Settings
  // ============================================================================

  getOrganisationSettings: async (
    token: string,
    organisationId: string,
  ): Promise<OrganisationSettings> => {
    const org = await portalFetch<
      Organisation & {
        billing_email?: string | null;
        billing_entity_name?: string | null;
        xero_contact_name?: string | null;
        vat_number?: string | null;
        vat_treatment?: VatTreatment | null;
        address_line1?: string | null;
        address_line2?: string | null;
        city?: string | null;
        state?: string | null;
        postal_code?: string | null;
        country?: string | null;
      }
    >(`/api/v1/organisations/${organisationId}`, { token });
    return {
      id: org.id,
      name: org.name,
      slug: org.slug,
      description: (org.description as string) || "",
      timezone: org.time_zone || "UTC",
      country_code: org.country_code,
      billing_currency: org.billing_currency,
      billing_email: org.billing_email ?? null,
      billing_entity_name: org.billing_entity_name ?? null,
      xero_contact_name: org.xero_contact_name ?? null,
      vat_number: org.vat_number ?? null,
      // Read-only here on purpose: the treatment is written by the admin-gated
      // `/billing` PATCH (billingApi.updateOrganisationBilling), never by this
      // generic organisation PATCH, so `updateOrganisationSettings` below does
      // not forward it.
      vat_treatment: org.vat_treatment ?? null,
      address_line1: org.address_line1 ?? null,
      address_line2: org.address_line2 ?? null,
      city: org.city ?? null,
      state: org.state ?? null,
      postal_code: org.postal_code ?? null,
      country: org.country ?? null,
    };
  },

  updateOrganisationSettings: async (
    token: string,
    organisationId: string,
    data: Partial<OrganisationSettings>,
  ): Promise<OrganisationSettings> => {
    const body: Record<string, unknown> = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.timezone !== undefined) body.time_zone = data.timezone;
    if (data.country_code !== undefined) body.country_code = data.country_code;
    if (data.billing_currency !== undefined)
      body.billing_currency = data.billing_currency;
    if (data.billing_email !== undefined)
      body.billing_email = data.billing_email;
    if (data.billing_entity_name !== undefined)
      body.billing_entity_name = data.billing_entity_name;
    // Omitted when undefined, as every field here is — the backend refuses a
    // blank value, and an org with no Xero contact yet must still be saveable.
    if (data.xero_contact_name !== undefined)
      body.xero_contact_name = data.xero_contact_name;
    if (data.vat_number !== undefined) body.vat_number = data.vat_number;
    if (data.address_line1 !== undefined)
      body.address_line1 = data.address_line1;
    if (data.address_line2 !== undefined)
      body.address_line2 = data.address_line2;
    if (data.city !== undefined) body.city = data.city;
    if (data.state !== undefined) body.state = data.state;
    if (data.postal_code !== undefined) body.postal_code = data.postal_code;
    if (data.country !== undefined) body.country = data.country;
    await portalFetch<Organisation>(
      `/api/v1/organisations/${organisationId}`,
      {
        token,
        method: "PATCH",
        body,
      },
    );
    return platformApi.getOrganisationSettings(token, organisationId);
  },

  // ============================================================================
  // Service Tiers (Platform Admin)
  // ============================================================================

  listAllTiers: (
    token: string,
    params?: {
      include_inactive?: boolean;
      service_id?: string;
      search?: string;
      offset?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.include_inactive) searchParams.append("include_inactive", "true");
    if (params?.service_id) searchParams.append("service_id", params.service_id);
    if (params?.search) searchParams.append("search", params.search);
    searchParams.append("offset", String(params?.offset || 0));
    searchParams.append("limit", String(params?.limit || 25));
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<ServiceTiersListResponse>(
      `/api/v1/platform/tiers/?${searchParams}`,
      { token },
    );
  },

  listTiersForService: (
    token: string,
    serviceId: string,
    includeInactive: boolean = false,
  ) =>
    portalFetch<ServiceTier[]>(
      `/api/v1/platform/tiers/service/${serviceId}?include_inactive=${includeInactive}`,
      { token },
    ),

  /**
   * The tiers of a service looked up by its NAME (cp `GET /tiers/service-by-name/{name}`,
   * no auth). A priced Xero workflow template is a service named by its
   * template key (cp W3, `workflow_template` category), so this is how the
   * templates page reads the price it bills at. 404 when the name has no
   * service — an unpriced template.
   */
  listTiersByServiceName: (serviceName: string) =>
    portalFetch<ServiceTier[]>(
      `/api/v1/platform/tiers/service-by-name/${encodeURIComponent(serviceName)}`,
      {},
    ),

  listPublicTiers: (serviceId?: string) => {
    const params = serviceId ? `?service_id=${serviceId}` : "";
    return portalFetch<ServiceTier[]>(`/api/v1/platform/tiers/public${params}`, {});
  },

  getTier: (token: string, tierId: string) =>
    portalFetch<ServiceTier>(`/api/v1/platform/tiers/${tierId}`, { token }),

  createTier: (token: string, data: ServiceTierCreateRequest) =>
    portalFetch<ServiceTier>("/api/v1/platform/tiers/", {
      token,
      method: "POST",
      body: data,
    }),

  updateTier: (token: string, tierId: string, data: ServiceTierUpdateRequest) =>
    portalFetch<ServiceTier>(`/api/v1/platform/tiers/${tierId}`, {
      token,
      method: "PUT",
      body: data,
    }),

  deleteTier: (token: string, tierId: string) =>
    portalFetch(`/api/v1/platform/tiers/${tierId}`, {
      token,
      method: "DELETE",
    }),

  seedTiers: (token: string, force: boolean = false) =>
    portalFetch<ServiceTier[]>("/api/v1/platform/tiers/seed", {
      token,
      method: "POST",
      body: { force },
    }),

  // ============================================================================
  // Service Features (Platform Admin)
  // ============================================================================

  listServiceFeatures: (
    token: string,
    serviceId: string,
    includeInactive: boolean = false,
  ) =>
    portalFetch<ServiceFeatureListResponse>(
      `/api/v1/service-features/service/${serviceId}?include_inactive=${includeInactive}`,
      { token },
    ),

  getServiceFeature: (token: string, featureId: string) =>
    portalFetch<ServiceFeature>(
      `/api/v1/service-features/${featureId}`,
      { token },
    ),

  createServiceFeature: (token: string, data: ServiceFeatureCreateRequest) =>
    portalFetch<ServiceFeature>("/api/v1/service-features/", {
      token,
      method: "POST",
      body: data,
    }),

  updateServiceFeature: (
    token: string,
    featureId: string,
    data: ServiceFeatureUpdateRequest,
  ) =>
    portalFetch<ServiceFeature>(`/api/v1/service-features/${featureId}`, {
      token,
      method: "PUT",
      body: data,
    }),

  listTierFeatures: (token: string, tierId: string) =>
    portalFetch<ServiceTierFeature[]>(
      `/api/v1/service-features/tier/${tierId}`,
      { token },
    ),

  addTierFeature: (token: string, tierId: string, featureId: string) =>
    portalFetch<ServiceTierFeature>(
      `/api/v1/service-features/tier/${tierId}/feature/${featureId}`,
      { token, method: "POST" },
    ),

  removeTierFeature: (token: string, tierId: string, featureId: string) =>
    portalFetch(`/api/v1/service-features/tier/${tierId}/feature/${featureId}`, {
      token,
      method: "DELETE",
    }),

  listFeatureAssignments: (
    token: string,
    organisationId: string,
    serviceSubscriptionId?: string,
  ) => {
    const params = new URLSearchParams();
    if (serviceSubscriptionId)
      params.append("service_subscription_id", serviceSubscriptionId);
    const query = params.toString();
    return portalFetch<ServiceFeatureAssignment[]>(
      `/api/v1/service-features/assignments/organisation/${organisationId}${query ? `?${query}` : ""}`,
      { token },
    );
  },

  createFeatureAssignment: (
    token: string,
    data: {
      service_feature_id: string;
      organisation_id: string;
      service_subscription_id?: string;
      service_item_subscription_id?: string;
      price_monthly_zar?: number;
      price_monthly_usd?: number;
    },
  ) =>
    portalFetch<ServiceFeatureAssignment>(
      "/api/v1/service-features/assignments",
      { token, method: "POST", body: data },
    ),

  cancelFeatureAssignment: (
    token: string,
    assignmentId: string,
    immediate: boolean = false,
  ) =>
    portalFetch<ServiceFeatureAssignment>(
      `/api/v1/service-features/assignments/${assignmentId}?immediate=${immediate}`,
      { token, method: "DELETE" },
    ),

  getEffectiveFeatures: (
    token: string,
    organisationId: string,
    serviceId: string,
  ) =>
    portalFetch<Record<string, unknown>[]>(
      `/api/v1/service-features/effective/organisation/${organisationId}/service/${serviceId}`,
      { token },
    ),

  getConnectorInstanceFeatures: (
    token: string,
    serviceId: string,
    clientConnectorId: number,
  ) =>
    portalFetch<Record<string, unknown>[]>(
      `/api/v1/service-features/connector/${serviceId}/instance/${clientConnectorId}`,
      { token },
    ),

  // ============================================================================
  // Organisation Discounts (Platform Admin)
  // ============================================================================

  listAllDiscounts: (
    token: string,
    params?: {
      organisation_id?: string;
      service_id?: string;
      include_inactive?: boolean;
      include_expired?: boolean;
      search?: string;
      offset?: number;
      limit?: number;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.organisation_id)
      searchParams.append("organisation_id", params.organisation_id);
    if (params?.service_id)
      searchParams.append("service_id", params.service_id);
    if (params?.include_inactive)
      searchParams.append("include_inactive", "true");
    if (params?.include_expired) searchParams.append("include_expired", "true");
    if (params?.search) searchParams.append("search", params.search);
    searchParams.append("offset", String(params?.offset || 0));
    searchParams.append("limit", String(params?.limit || 25));
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<OrganisationDiscountsListResponse>(
      `/api/v1/discounts/?${searchParams}`,
      { token },
    );
  },

  listOrganisationDiscounts: (
    token: string,
    organisationId: string,
    params?: { include_inactive?: boolean; include_expired?: boolean },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.include_inactive)
      searchParams.append("include_inactive", "true");
    if (params?.include_expired) searchParams.append("include_expired", "true");
    const query = searchParams.toString() ? `?${searchParams.toString()}` : "";
    return portalFetch<OrganisationDiscount[]>(
      `/api/v1/discounts/organisation/${organisationId}${query}`,
      { token },
    );
  },

  getDiscountSummary: (token: string, organisationId: string) =>
    portalFetch<DiscountSummary>(
      `/api/v1/discounts/organisation/${organisationId}/summary`,
      { token },
    ),

  getDiscount: (token: string, discountId: string) =>
    portalFetch<OrganisationDiscount>(`/api/v1/discounts/${discountId}`, {
      token,
    }),

  createDiscount: (token: string, data: OrganisationDiscountCreateRequest) =>
    portalFetch<OrganisationDiscount>("/api/v1/discounts/", {
      token,
      method: "POST",
      body: data,
    }),

  updateDiscount: (
    token: string,
    discountId: string,
    data: OrganisationDiscountUpdateRequest,
  ) =>
    portalFetch<OrganisationDiscount>(`/api/v1/discounts/${discountId}`, {
      token,
      method: "PUT",
      body: data,
    }),

  deactivateDiscount: (token: string, discountId: string) =>
    portalFetch(`/api/v1/discounts/${discountId}`, {
      token,
      method: "DELETE",
    }),

  deleteDiscountPermanently: (token: string, discountId: string) =>
    portalFetch(`/api/v1/discounts/${discountId}/permanent`, {
      token,
      method: "DELETE",
    }),

  // ============================================================================
  // VPN (WireGuard)
  // ============================================================================

  listVpnPeers: (
    token: string,
    params?: {
      page?: number;
      per_page?: number;
      search?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
      peer_type?: VpnPeerType;
      status?: "active" | "revoked" | "all";
    },
  ) => {
    const searchParams = new URLSearchParams({
      page: String(params?.page || 1),
      per_page: String(params?.per_page || 25),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    if (params?.peer_type) searchParams.append("peer_type", params.peer_type);
    if (params?.status) searchParams.append("status", params.status);
    return portalFetch<VpnPeersListResponse>(
      `/api/v1/gateway/wireguard/api/v1/vpn/peers?${searchParams}`,
      { token },
    );
  },

  createVpnPeer: (token: string, data: VpnPeerCreateRequest) =>
    portalFetch<VpnPeer>("/api/v1/gateway/wireguard/api/v1/vpn/peers", {
      token,
      method: "POST",
      body: data,
    }),

  getVpnPeer: (token: string, peerId: string) =>
    portalFetch<VpnPeer>(`/api/v1/gateway/wireguard/api/v1/vpn/peers/${peerId}`, { token }),

  revokeVpnPeer: (token: string, peerId: string) =>
    portalFetch(`/api/v1/gateway/wireguard/api/v1/vpn/peers/${peerId}`, {
      token,
      method: "DELETE",
    }),

  reviveVpnPeer: (token: string, peerId: string) =>
    portalFetch<VpnPeer>(`/api/v1/gateway/wireguard/api/v1/vpn/peers/${peerId}/revive`, {
      token,
      method: "POST",
    }),

  getVpnPeerConfig: (token: string, peerId: string) =>
    portalFetch<VpnPeerConfigResponse>(`/api/v1/gateway/wireguard/api/v1/vpn/peers/${peerId}/config`, {
      token,
    }),

  getVpnStatus: (token: string) =>
    portalFetch<VpnStatusResponse>("/api/v1/gateway/wireguard/api/v1/vpn/status", { token }),

  // ============================================================================
  // VPN (IPsec) — read-only, platform admins only (the wireguard API 403s
  // everyone else)
  // ============================================================================

  listIpsecConnections: (token: string) =>
    portalFetch<IpsecConnectionsListResponse>(
      "/api/v1/gateway/wireguard/api/v1/vpn/ipsec/connections",
      { token },
    ),

  // ============================================================================
  // DynDNS
  // ============================================================================

  listDynDnsDomains: (
    token: string,
    params?: {
      page?: number;
      page_size?: number;
      search?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    // Convert page/page_size to offset/limit for the backend
    const page = params?.page || 1;
    const pageSize = params?.page_size || 25;
    const offset = (page - 1) * pageSize;
    const searchParams = new URLSearchParams({
      limit: String(pageSize),
      offset: String(offset),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<DynDnsDomainsListResponse>(
      `/api/v1/gateway/dyndns/api/v1/domains?${searchParams}`,
      { token },
    );
  },

  createDynDnsDomain: (token: string, data: DynDnsDomainCreateRequest) =>
    portalFetch<DynDnsDomainCreateResponse>("/api/v1/gateway/dyndns/api/v1/domains", {
      token,
      method: "POST",
      body: data,
    }),

  getDynDnsDomain: (token: string, domainId: string) =>
    portalFetch<DynDnsDomain>(`/api/v1/gateway/dyndns/api/v1/domains/${domainId}`, { token }),

  updateDynDnsDomain: (token: string, domainId: string, data: DynDnsDomainUpdateRequest) =>
    portalFetch<DynDnsDomain>(`/api/v1/gateway/dyndns/api/v1/domains/${domainId}`, {
      token,
      method: "PATCH",
      body: data,
    }),

  deleteDynDnsDomain: (token: string, domainId: string) =>
    portalFetch(`/api/v1/gateway/dyndns/api/v1/domains/${domainId}`, {
      token,
      method: "DELETE",
    }),

  regenerateDynDnsApiKey: (token: string, domainId: string) =>
    portalFetch<DynDnsApiKeyResponse>(
      `/api/v1/gateway/dyndns/api/v1/domains/${domainId}/regenerate-key`,
      {
        token,
        method: "POST",
      },
    ),

  getDynDnsDomainLogs: (
    token: string,
    domainId: string,
    params?: { page?: number; page_size?: number },
  ) => {
    // Convert page/page_size to offset/limit for the backend
    const page = params?.page || 1;
    const pageSize = params?.page_size || 25;
    const offset = (page - 1) * pageSize;
    const searchParams = new URLSearchParams({
      limit: String(pageSize),
      offset: String(offset),
    });
    return portalFetch<DynDnsUpdateLogsListResponse>(
      `/api/v1/gateway/dyndns/api/v1/domains/${domainId}/logs?${searchParams}`,
      { token },
    );
  },

  // ============================================================================
  // Messaging
  // ============================================================================

  getMessagingHealth: (token: string) =>
    portalFetch<MessagingHealthResponse>(
      "/api/v1/gateway/messaging/health",
      { token },
    ),

  listChannelConfigs: (
    token: string,
    organisationId: string,
    params?: {
      offset?: number;
      limit?: number;
      search?: string;
      sort_by?: string;
      sort_order?: "asc" | "desc";
    },
  ) => {
    const searchParams = new URLSearchParams({
      offset: String(params?.offset || 0),
      limit: String(params?.limit || 25),
    });
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order) searchParams.append("sort_order", params.sort_order);
    return portalFetch<ChannelConfigsListResponse>(
      `/api/v1/gateway/messaging/api/v1/channels/org/${organisationId}?${searchParams}`,
      { token },
    );
  },

  createChannelConfig: (
    token: string,
    organisationId: string,
    data: ChannelConfigCreateRequest,
  ) =>
    portalFetch<ChannelConfig>(
      `/api/v1/gateway/messaging/api/v1/channels/org/${organisationId}`,
      {
        token,
        method: "POST",
        body: data,
      },
    ),

  updateChannelConfig: (
    token: string,
    channelId: string,
    data: { name?: string; is_active?: boolean; config?: Record<string, string> },
  ) =>
    portalFetch<ChannelConfig>(
      `/api/v1/gateway/messaging/api/v1/channels/${channelId}`,
      { token, method: "PUT", body: data },
    ),

  deleteChannelConfig: (token: string, channelId: string) =>
    portalFetch(
      `/api/v1/gateway/messaging/api/v1/channels/${channelId}`,
      {
        token,
        method: "DELETE",
      },
    ),

  setDefaultChannelConfig: (token: string, channelId: string) =>
    portalFetch<ChannelConfig>(
      `/api/v1/gateway/messaging/api/v1/channels/${channelId}/set-default`,
      { token, method: "POST" },
    ),

  // WhatsApp phone-number registration + live status
  registerWhatsAppPhone: (token: string, channelId: string) =>
    portalFetch<ChannelConfig>(
      `/api/v1/gateway/messaging/api/v1/channels/${channelId}/register-phone`,
      { token, method: "POST" },
    ),

  getWhatsAppPhoneStatus: (token: string, channelId: string) =>
    portalFetch<WhatsAppPhoneStatus>(
      `/api/v1/gateway/messaging/api/v1/channels/${channelId}/phone-status`,
      { token },
    ),

  // The verify_token is redacted in normal channel responses; fetch the real
  // value on demand for the Meta webhook setup display.
  revealVerifyToken: (token: string, channelId: string) =>
    portalFetch<{ verify_token: string; webhook_url: string }>(
      `/api/v1/gateway/messaging/api/v1/channels/${channelId}/verify-token`,
      { token },
    ),

  // Templates
  listTemplates: (token: string, organisationId: string) =>
    portalFetch<MessageTemplate[]>(
      `/api/v1/gateway/messaging/api/v1/templates/org/${organisationId}`,
      { token },
    ),

  syncWhatsAppTemplates: (token: string, organisationId: string) =>
    portalFetch<TemplateSyncResult>(
      `/api/v1/gateway/messaging/api/v1/templates/org/${organisationId}/sync-whatsapp`,
      { token, method: "POST" },
    ),

  submitWhatsAppTemplate: (token: string, templateId: string) =>
    portalFetch<TemplateSubmitResult>(
      `/api/v1/gateway/messaging/api/v1/templates/${templateId}/submit-whatsapp`,
      { token, method: "POST" },
    ),

  // Conversations / unified inbox
  listConversations: (
    token: string,
    organisationId: string,
    params?: { limit?: number; offset?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.append("limit", String(params.limit));
    if (params?.offset !== undefined) qs.append("offset", String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return portalFetch<MessagingConversationsListResponse>(
      `/api/v1/gateway/messaging/api/v1/conversations/org/${organisationId}${suffix}`,
      { token },
    );
  },

  listConversationMessages: (
    token: string,
    conversationId: string,
    params?: { limit?: number; offset?: number },
  ) => {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.append("limit", String(params.limit));
    if (params?.offset !== undefined) qs.append("offset", String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return portalFetch<MessagingMessagesListResponse>(
      `/api/v1/gateway/messaging/api/v1/conversations/${conversationId}/messages${suffix}`,
      { token },
    );
  },

  sendConversationMessage: (
    token: string,
    conversationId: string,
    body: {
      content: string;
      content_type?: string;
      template_name?: string;
      // ME41: the backend has accepted this since schemas.py:266 but the FE type
      // omitted it, so a template could be named and never filled in — WhatsApp
      // rejects a template whose placeholders are unsupplied, which reads as
      // another mystery failure on the exact path this feature exists to fix.
      template_variables?: Record<string, string>;
    },
  ) =>
    portalFetch<{ message: string }>(
      `/api/v1/gateway/messaging/api/v1/conversations/${conversationId}/messages`,
      { token, method: "POST", body },
    ),

  markConversationRead: (token: string, conversationId: string) =>
    portalFetch<{ message: string }>(
      `/api/v1/gateway/messaging/api/v1/conversations/${conversationId}/mark-read`,
      { token, method: "POST" },
    ),

  // ── Inbound dead letters (ME26) ────────────────────────────────────────
  // A parked row is a customer's inbound WhatsApp message that is PERMANENTLY
  // lost: the webhook already ACKed 200 and forfeited Meta's redelivery, so the
  // parked payload is the only surviving copy. Replay is the only way back.
  // Platform-admin only — these read and re-inject OTHER tenants' messages.

  listInboundDeadLetters: (
    token: string,
    params: { organisationId?: string; includeResolved?: boolean; limit?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.organisationId) query.set("organisation_id", params.organisationId);
    if (params.includeResolved) query.set("include_resolved", "true");
    // The API defaults to 100 and caps at 500. Ask for the cap: every row is a
    // lost customer message, and a page that silently shows the first 100 of
    // 250 reads as "that is the whole backlog".
    query.set("limit", String(params.limit ?? DEAD_LETTER_PAGE_LIMIT));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return portalFetch<InboundDeadLetter[]>(
      `/api/v1/gateway/messaging/api/v1/dead-letters${suffix}`,
      { token },
    );
  },

  getInboundDeadLetter: (token: string, id: string) =>
    portalFetch<InboundDeadLetterDetail>(
      `/api/v1/gateway/messaging/api/v1/dead-letters/${id}`,
      { token },
    ),

  replayInboundDeadLetter: (token: string, id: string) =>
    portalFetch<InboundDeadLetterActionResult>(
      `/api/v1/gateway/messaging/api/v1/dead-letters/${id}/replay`,
      { token, method: "POST" },
    ),

  resolveInboundDeadLetter: (token: string, id: string) =>
    portalFetch<InboundDeadLetterActionResult>(
      `/api/v1/gateway/messaging/api/v1/dead-letters/${id}/resolve`,
      { token, method: "POST" },
    ),

  // ============================================================================
  // Timezones
  // ============================================================================

  listTimezones: (
    token: string,
    params?: {
      search?: string;
      region?: string;
      country?: string;
      limit?: number;
      offset?: number;
    },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.region) searchParams.append("region", params.region);
    if (params?.country) searchParams.append("country", params.country);
    if (params?.limit) searchParams.append("limit", String(params.limit));
    if (params?.offset) searchParams.append("offset", String(params.offset));
    const qs = searchParams.toString();
    return portalFetch<TimezoneListResponse>(
      `/api/v1/timezones/${qs ? `?${qs}` : ""}`,
      { token },
    );
  },

  // ============================================================================
  // Countries
  // ============================================================================

  /**
   * `cp01.Country` — the ISO-3166 set with VAT treatment and default currency.
   *
   * `limit` defaults to 300 because the endpoint's ceiling is 300 and the table
   * is ~249 fixed rows: the picker wants all of them in one round trip. The
   * shared 100-row cap would silently truncate at the letter I and lose Rwanda,
   * South Africa and everything after them.
   *
   * Requested WITHOUT a trailing slash to match the route as registered
   * (`@router.get("")` under prefix `/countries`).
   */
  listCountries: (
    token: string,
    params?: {
      search?: string;
      include_inactive?: boolean;
      limit?: number;
      offset?: number;
    },
  ) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.include_inactive) searchParams.append("include_inactive", "true");
    searchParams.append("limit", String(params?.limit ?? 300));
    if (params?.offset) searchParams.append("offset", String(params.offset));
    return portalFetch<CountryListResponse>(
      `/api/v1/countries?${searchParams.toString()}`,
      { token },
    );
  },
};
